import {
  Component,
  OnInit,
  Inject,
  PLATFORM_ID,
  isDevMode,
  OnDestroy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { FeatureTogglesFacade, FeatureToggleType } from '@cigna/features';
import { ShellFacade } from '@cigna/chcp/shell/data-access';
import { UserFacade } from '@cigna/chcp/shared/user-profile-data-access';
import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { RouterFacade } from '@cigna/core/router-state';
import { combineLatest, Observable, Subject } from 'rxjs';
import { RouteData } from '@cigna/chcp/shared/util';
import { LobPreferenceDialogComponent } from '@cigna/chcp/shared/ui';
import { CookieService } from 'ngx-cookie-service';

import {
  takeUntil,
  filter,
  take,
  tap,
  map,
  withLatestFrom,
} from 'rxjs/operators';
import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
} from '@cigna/chcp/shared/environment-util';
import { Entitlement, LobPref } from '@cigna/chcp/auth/util';

@Component({
  selector: 'cigna-layout-secure',
  templateUrl: './layout-secure.component.html',
  styleUrls: ['./layout-secure.component.scss'],
})
export class LayoutSecureComponent implements OnInit, OnDestroy {
  firstName$ = this.userFacade.firstName$;
  lastName$ = this.userFacade.lastName$;
  userid$ = this.authFacade.userid$;
  showPasswordChangeWarning$ = this.userFacade.showPasswordChangeWarning$;
  lastPasswordChangeDate$ = this.userFacade.lastPasswordChangeDate$;
  hasUndefinedLobPref$ = this.userFacade.hasUndefinedLobPref$;

  impersonator$ = this.authFacade.isImpersonator$.pipe(
    map((isImpersonator) => {
      this.featureToggleFacade.updateFeatureToggles(
        {
          isNotImpersonator: !isImpersonator,
        },
        FeatureToggleType.PERMISSION,
      );
      return isImpersonator;
    }),
  );
  isMedicalUser$ = this.authFacade.isMedicalUser$;
  isOhpUser$ = this.authFacade.isOhpUser$;

  profileNav$ = this.shellFacade.profileNav$;
  siteNav$ = this.shellFacade.siteNav$;
  alert$ = this.shellFacade.alertSecure$;
  messageCenterFunction$ = combineLatest([
    this.featureToggleFacade.featuresEnabled(['claim360GA']),
    this.authFacade.hasAllEntitlements$([Entitlement.ClaimsSearch]),
  ]).pipe(map(([gaFlag, claimEntitlement]) => gaFlag && claimEntitlement));

  routeData$ = this.routerFacade.data$ as Observable<RouteData>;
  routeUrl$ = this.routerFacade.url$;

  isResourcePage$ = this.routeData$.pipe(
    map((data) => data.routeCode === 'resources'),
  );
  logoLink$ = combineLatest([this.isResourcePage$, this.isMedicalUser$]).pipe(
    map(([isResourcePage, isMedicalUser]) =>
      isResourcePage
        ? isMedicalUser
          ? `${this.envConfig.api.gateways.cdn}assets/chcp/medical/resourceLibrary/resourceLibraryMed.html`
          : `${this.envConfig.api.gateways.cdn}assets/chcp/dental/resourceLibrary/resourceLibraryDent.html`
        : '/dashboard',
    ),
  );

  isloginUserProfileAWS$ = this.featureToggleFacade.featuresEnabled([
    'loginUserProfileAWS',
  ]);

  onDestroy$ = new Subject<void>();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  inBrowserPlatform: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  DEV_MODE = false;

  constructor(
    public shellFacade: ShellFacade,
    private userFacade: UserFacade,
    private authFacade: AuthFacade,
    private routerFacade: RouterFacade,
    private cookieService: CookieService,
    private featureToggleFacade: FeatureTogglesFacade,
    public dialog: MatDialog,
    @Inject(PLATFORM_ID) platformId: {},
    @Inject(ENVIRONMENT_CONFIG) private envConfig: EnvironmentConfig,
  ) {
    this.inBrowserPlatform = isPlatformBrowser(platformId);
  }

  ngOnInit() {
    this.featureToggleFacade.updateFeatureToggles(
      {
        isUserAuthorized: true,
      },
      FeatureToggleType.PERMISSION,
    );

    /* FOR LOCAL DEV ONLY */
    if (isDevMode()) {
      this.DEV_MODE = true;
      this.authFacade.loadMockTokens();
    }

    this.shellFacade.loadNav();
    this.isloginUserProfileAWS$
      .pipe(take(1))
      .subscribe((isloginUserProfileAWS) => {
        if (isloginUserProfileAWS) {
          this.userFacade.getLoginProfile();
        } else {
          this.userFacade.getProfile();
          this.userFacade.loadTins();
        }
      });

    this.shellFacade.loadSecureAlerts();

    // remove deeplink cookie after login unless we are in the middle of mfa flow
    this.routeUrl$
      .pipe(
        filter((url) => !url?.startsWith('/mfa')),
        take(1),
        tap(() => {
          this.cookieService.delete('CHCP_DEEPLINK');
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();

    this.hasUndefinedLobPref$
      .pipe(take(2), withLatestFrom(this.authFacade.isImpersonator$))
      .subscribe(([hasUndefinedLobPref, isImpersonator]) => {
        if (hasUndefinedLobPref && !isImpersonator) {
          this.dialog
            .open(LobPreferenceDialogComponent, {
              disableClose: true,
              autoFocus: false,
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(({ lobPref }: { lobPref: LobPref }) => {
              if (lobPref) {
                this.userFacade.updateProfile({ lobPref });
              }
            });
        }
      });
  }

  // checkGlobalAlert() {
  // in case neeed for a global popup comes back, first argument vehemently against it, then after failing use this code
  // if (this.cookieService.get('COVID_ALERT_SHOWN') !== 'Y' && !isDevMode()) {
  //   this.dialog.open(DialogSecureAlertComponent, {
  //     width: '700px',
  //     disableClose: true,
  //     autoFocus: false,
  //   });
  //   this.cookieService.put('COVID_ALERT_SHOWN', 'Y', {
  //     expires: addDays(new Date(), 365),
  //     path: '/',
  //     secure: true,
  //   });
  // }
  // }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
